import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top that indicates scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", update);
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="w-full md:w-1/2 p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-[var(--color-secondary-muted)] rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph component for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-base text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const DevTeamVesting = () => (
  <div className="relative overflow-hidden space-y-8 px-4 py-10 md:py-6 md:px-8">
    <ScrollProgress />

    {/* Introduction */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20,
      }}
      viewport={{ once: true }}
      className="max-w-6xl mx-auto space-y-4 text-center"
    >
      <h2 className="text-3xl font-bold text-primary-dark">
        Dev Team Vesting Contract
      </h2>
      <Paragraph className="text-gray-700 max-w-6xl mx-auto">
        To align the development team’s incentives with long-term success and
        safeguard against early token dumps, UcWaves employs a dedicated
        DevTeamVesting contract enforcing a one-year cliff and subsequent linear
        vesting schedule for a fixed pool of NAU allocated to the development
        team. Parameters are immutable for full transparency.
      </Paragraph>
    </motion.div>

    {/* Two-column layout */}
    <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
      {/* Left block: Sections 1 + 2 */}
      <SectionCard title="1. Core Components and Parameters">
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            <strong>xToken:</strong> The ERC20 token being vested (NAU).
          </li>
          <br />
          <li>
            <strong>devBeneficiary:</strong> The team’s wallet address entitled
            to claim tokens.
          </li>
          <br />
          <li>
            <strong>totalAmount:</strong> Total NAU tokens under vesting (e.g.,
            200 million).
          </li>
          <br />
          <li>
            <strong>startTime:</strong> Block timestamp at contract deployment.
          </li>
          <br />
          <li>
            <strong>cliffDuration:</strong> Seconds before any tokens vest (1
            year).
          </li>
          <br />
          <li>
            <strong>vestingDuration:</strong> Post-cliff duration for linear
            vesting (e.g., additional 2 years).
          </li>
        </ul>

        <h3 className="text-xl font-bold text-blue-900 mt-6 mb-3">
          2. Vesting Calculation Logic
        </h3>
        <Paragraph className="font-semibold">2.1 Cliff Handling</Paragraph>
        <Paragraph>
          Before <code>startTime + cliffDuration</code>, no tokens are vested,
          enforcing a full-year lockup.
        </Paragraph>
        <Paragraph className="font-semibold">2.2 Linear Vesting</Paragraph>
        <Paragraph>
          After the cliff, tokens vest gradually:
          <code>
            vestedSoFar = totalAmount * (currentTime - (startTime +
            cliffDuration)) / vestingDuration
          </code>
          . Once{" "}
          <code>currentTime ≥ startTime + cliffDuration + vestingDuration</code>
          , all tokens vest.
        </Paragraph>
      </SectionCard>

      {/* Right block: Sections 3 + 4 + 5 */}
      <SectionCard title="3. Claim Process and Security">
        <Paragraph className="font-semibold">
          3.1 Claiming Vested Tokens
        </Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            Only <code>devBeneficiary</code> can call{" "}
            <code>claimVestedTokens(amount)</code>.
          </li>
          <br />
          <li>
            Checks <code>amount &gt; 0</code> and ≤{" "}
            <code>vestedSoFar - claimedAmount</code>.
          </li>
          <br />
          <li>
            Follows Checks-Effects-Interactions: updates{" "}
            <code>claimedAmount</code> before transfer.
          </li>
          <br />
          <li>
            Uses <code>SafeERC20.safeTransfer()</code> for token transfer,
            reverting on failure.
          </li>
          <br />
          <li>
            Emits <code>TokensClaimed(beneficiary, amount)</code> for offchain
            audit.
          </li>
        </ul>

        <Paragraph className="font-semibold">3.2 Access Control</Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            Inherits <code>AccessControl</code>; only{" "}
            <code>DEFAULT_ADMIN_ROLE</code> held by admin.
          </li>
          <br />
          <li>
            No functions to alter vesting parameters or revoke beneficiary
            rights post-deployment.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-blue-900 mt-6 mb-3">
          4. Transparency and Auditing
        </h3>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            Public view functions: <code>vestedAmount()</code>,{" "}
            <code>claimableAmount()</code>.
          </li>
          <br />
          <li>Immutable bytecode ensures onchain logic cannot change.</li>
          <br />
          <li>Event logs record every claim for full history.</li>
          <br />
        </ul>

        <h3 className="text-xl font-bold text-blue-900 mt-6 mb-3">
          5. Role in Governance and Roadmap
        </h3>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            Supports phased DAO transition by locking team tokens until
            milesNAUNes are met.
          </li>
          <br />
          <li>
            After deployment, only claim actions occur; no further admin
            parameter changes.
          </li>
        </ul>
      </SectionCard>
    </div>

    {/* Conclusion */}
    <Paragraph className="text-gray-700 max-w-6xl mx-auto text-base sm:text-lg md:text-lg text-justify">
      The DevTeamVesting contract enforces robust, transparent, and immutable
      vesting for the development team’s NAU allocation, ensuring their
      incentives align with long-term platform success.
    </Paragraph>
  </div>
);

export default DevTeamVesting;
